<template lang="pug">
  .main
    base-header(ref="jello").pb-1
      b-row.py-4(align-v='center')
        b-col(cols='7' lg='6')
          h6.h2.text-white.d-inline-block.mb-0 Sachmerkmale

    b-container(fluid, v-if="checklists")
      b-row.mt-4
        b-col(cols='12' lg='4')
          .d-flex.justify-content-between.align-items-center.mb-3
            h5.mb-0 Checklisten
            b-button(variant="secondary", size="sm", @click="createChecklist") Neu
          .list-group
            a.list-group-item.list-group-item-action(
              v-for="item in checklists",
              :key="item._id",
              :class="{ active: selected && selected._id === item._id }",
              @click.prevent="selectChecklist(item)"
            )
              .d-flex.justify-content-between.align-items-center
                span {{ item.name }}
                b-badge(variant="light") {{ (item.checklist_entries || []).length }}

          .mt-3
            b-button(variant="outline-secondary", size="sm", @click="importFromDocsense", :disabled="importing")
              | {{ importing ? 'Importiere...' : 'Von DocSense importieren' }}

        b-col(cols='12' lg='8', v-if="selected")
          b-card
            b-form(@submit.prevent="saveChecklist")
              b-form-group(label="Name")
                b-form-input(v-model="selected.name", required)

              b-form-group(label="Beschreibung (Prompt-Kontext)")
                b-form-textarea(v-model="selected.description", rows="4")

              b-row
                b-col(cols='6')
                  b-form-group(label="DCC-Klassen (kommagetrennt)")
                    b-form-input(v-model="selected.metadata", placeholder="z.B. DA010,AA010")
                b-col(cols='6')
                  b-form-group(label="Folge-Checkliste")
                    b-form-select(v-model="selected.follow_up_checklist_id")
                      b-form-select-option(:value="null") Keine
                      b-form-select-option(
                        v-for="item in otherChecklists",
                        :key="item._id",
                        :value="item._id"
                      ) {{ item.name }}

              hr
              .d-flex.justify-content-between.align-items-center.mb-2
                h5.mb-0 Einträge
                b-button(variant="secondary", size="sm", @click="addEntry") Eintrag hinzufügen

              .border.rounded.p-3.mb-3(v-for="(entry, idx) in selected.checklist_entries", :key="idx")
                b-row
                  b-col(cols='5')
                    b-form-group(label="Sachmerkmal-Key")
                      b-form-input(v-model="entry.item_to_be_checked", required)
                  b-col(cols='4')
                    b-form-group(label="Datentyp")
                      b-form-select(v-model="entry.output_type")
                        b-form-select-option(value="string") Text (string)
                        b-form-select-option(value="integer") Ganzzahl (integer)
                        b-form-select-option(value="float") Dezimalzahl (float)
                        b-form-select-option(value="boolean") Ja/Nein (boolean)
                        b-form-select-option(value="dict") Objekt (dict)
                  b-col(cols='3').text-right
                    b-button(variant="danger", size="sm", @click="removeEntry(idx)")
                      i.fa.fa-trash

                b-form-group(label="Extraktions-Hinweis (Comment/Prompt)")
                  b-form-textarea(v-model="entry.comment", rows="2")

                div(v-if="entry.output_type === 'dict'")
                  .d-flex.justify-content-between.align-items-center.mb-2
                    .small.text-muted Dict-Felder
                    b-button(variant="link", size="sm", @click="addDictField(entry)") Feld hinzufügen
                  b-row.mb-2(v-for="(desc, fieldKey) in entry.output_type_specifications", :key="fieldKey")
                    b-col(cols='4')
                      b-form-input(
                        :value="fieldKey",
                        @input="renameDictField(entry, fieldKey, $event)",
                        placeholder="Feldname"
                      )
                    b-col(cols='7')
                      b-form-input(v-model="entry.output_type_specifications[fieldKey]", placeholder="Beschreibung für KI")
                    b-col(cols='1')
                      b-button(variant="link", size="sm", @click="removeDictField(entry, fieldKey)")
                        i.fa.fa-times

              .d-flex.justify-content-between.mt-4
                b-button(variant="danger", @click="deleteChecklist") Löschen
                b-button(variant="primary", type="submit", :disabled="saving") Speichern
</template>

<script>
export default {
  data() {
    return {
      checklists: null,
      selected: null,
      saving: false,
      importing: false,
    };
  },
  computed: {
    otherChecklists() {
      if (!this.selected) return this.checklists || [];
      return (this.checklists || []).filter(c => c._id !== this.selected._id);
    },
  },
  methods: {
    normalizeChecklist(data) {
      return {
        ...data,
        checklist_entries: (data.checklist_entries || []).map(entry => ({
          item_to_be_checked: entry.item_to_be_checked || '',
          comment: entry.comment || '',
          output_type: entry.output_type || 'string',
          output_type_specifications: { ...(entry.output_type_specifications || {}) },
        })),
      };
    },

    selectChecklist(item) {
      this.selected = this.normalizeChecklist(this.$_.cloneDeep(item));
    },

    async fetch() {
      const response = await this.axios.get(this.base_url + '/sachmerkmale/checklists');
      this.checklists = response.data || [];
      if (this.selected) {
        const updated = this.checklists.find(c => c._id === this.selected._id);
        if (updated) {
          this.selected = this.normalizeChecklist(this.$_.cloneDeep(updated));
        } else {
          this.selected = null;
        }
      }
    },

    createChecklist() {
      this.selected = this.normalizeChecklist({
        _id: null,
        name: 'Neue Checkliste',
        description: '',
        metadata: '',
        follow_up_checklist_id: null,
        checklist_entries: [],
      });
    },

    addEntry() {
      this.selected.checklist_entries.push({
        item_to_be_checked: '',
        comment: '',
        output_type: 'string',
        output_type_specifications: {},
      });
    },

    removeEntry(idx) {
      this.selected.checklist_entries.splice(idx, 1);
    },

    addDictField(entry) {
      if (!entry.output_type_specifications) {
        this.$set(entry, 'output_type_specifications', {});
      }
      let i = 1;
      let key = 'Feld';
      while (entry.output_type_specifications[key]) {
        i += 1;
        key = 'Feld' + i;
      }
      this.$set(entry.output_type_specifications, key, '');
    },

    renameDictField(entry, oldKey, newKey) {
      if (!newKey || oldKey === newKey) return;
      const value = entry.output_type_specifications[oldKey];
      this.$delete(entry.output_type_specifications, oldKey);
      this.$set(entry.output_type_specifications, newKey, value);
    },

    removeDictField(entry, fieldKey) {
      this.$delete(entry.output_type_specifications, fieldKey);
    },

    payloadFromSelected() {
      return {
        name: this.selected.name,
        description: this.selected.description || '',
        metadata: this.selected.metadata || '',
        base_prompt: this.selected.base_prompt || '',
        follow_up_checklist_id: this.selected.follow_up_checklist_id || null,
        checklist_entries: this.selected.checklist_entries.map(entry => ({
          item_to_be_checked: entry.item_to_be_checked,
          comment: entry.comment || null,
          output_type: entry.output_type || 'string',
          output_type_specifications: entry.output_type === 'dict'
            ? (entry.output_type_specifications || {})
            : {},
        })),
      };
    },

    async saveChecklist() {
      this.saving = true;
      try {
        const payload = this.payloadFromSelected();
        if (this.selected._id) {
          await this.axios.put(
            this.base_url + '/sachmerkmale/checklists/' + this.selected._id,
            payload
          );
        } else {
          const response = await this.axios.post(
            this.base_url + '/sachmerkmale/checklists',
            payload
          );
          this.selected._id = response.data._id;
        }
        this.$bvToast.toast('Checkliste gespeichert', {
          title: 'Gespeichert',
          variant: 'primary',
          autoHideDelay: 1500,
        });
        await this.fetch();
      } finally {
        this.saving = false;
      }
    },

    deleteChecklist() {
      if (!this.selected || !this.selected._id) {
        this.selected = null;
        return;
      }
      this.$bvModal.msgBoxConfirm('Soll die Checkliste "' + this.selected.name + '" wirklich gelöscht werden?')
        .then(async (value) => {
          if (!value) return;
          await this.axios.delete(this.base_url + '/sachmerkmale/checklists/' + this.selected._id);
          this.selected = null;
          await this.fetch();
        });
    },

    async importFromDocsense() {
      this.importing = true;
      try {
        const response = await this.axios.post(this.base_url + '/sachmerkmale/checklists/import-from-docsense');
        this.checklists = response.data.checklists || [];
        this.$bvToast.toast(response.data.imported + ' Checklisten importiert', {
          title: 'Import abgeschlossen',
          variant: 'primary',
          autoHideDelay: 2000,
        });
      } finally {
        this.importing = false;
      }
    },
  },
  async mounted() {
    await this.fetch();
  },
};
</script>

<style scoped>
.list-group-item.active {
  background-color: #003c50;
  border-color: #003c50;
}
</style>
